import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSession } from '../../state/useSession';
import { Button, Screen, TextField } from '../../ui';
import { PASSWORD_RULE } from './passwordRule';

/**
 * Two phases in one screen, mirroring the app: ask for a code, then set the new password.
 * Staying on one screen keeps the email the person typed, which a second route would lose.
 */
export default function ForgotPassword() {
  const { forgotPassword, confirmForgotPassword } = useSession();
  const navigate = useNavigate();
  /*
   * Where sign-in was sending them, passed back to it. Somebody with an account who taps an ad
   * and has forgotten the password came through here and was then signed in to the home page,
   * with the idea the ad carried left behind.
   */
  const from = (useLocation().state as { from?: string } | null)?.from;
  const [phase, setPhase] = useState<'request' | 'confirm'>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function request(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await forgotPassword(email.trim());
      setPhase('confirm');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send a reset code.');
    } finally {
      setBusy(false);
    }
  }

  async function confirm(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await confirmForgotPassword(email.trim(), code.trim(), password);
      // The email they just typed, filled in on Sign in (F7).
      navigate('/sign-in', { replace: true, state: { email: email.trim(), ...(from ? { from } : {}) } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reset your password.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen title="Reset your password">
      {phase === 'request' ? (
        <form onSubmit={request} noValidate>
          <TextField label="Email" type="email" autoComplete="email" value={email} onChangeText={setEmail} />
          {error && <p role="alert" className="type-caption field-error-text">{error}</p>}
          <Button title="Send code" type="submit" loading={busy} />
        </form>
      ) : (
        <form onSubmit={confirm} noValidate>
          <p role="status" className="type-body">We sent a code to {email}.</p>
          <TextField
            label="Code"
            value={code}
            onChangeText={setCode}
            placeholder="123456"
            autoComplete="one-time-code"
            inputMode="numeric"
          />
          <TextField
            label="New password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChangeText={setPassword}
            hint={PASSWORD_RULE}
          />
          {error && <p role="alert" className="type-caption field-error-text">{error}</p>}
          <Button title="Set new password" type="submit" loading={busy} />
        </form>
      )}
    </Screen>
  );
}
